'use client';

import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { create } from '@bufbuild/protobuf';
import { TimestampSchema } from '@bufbuild/protobuf/wkt';
import { Box, Flex, Stack, Text, VStack, type DateValue } from '@chakra-ui/react';
import { Button } from '@m/components-ui/button';
import {
  DatePicker,
  dateToDateValue,
  dateValueToDate,
} from '@m/components-ui/date-and-time/date-picker';
import {
  getSelectableTimeOptions,
  snapToSelectableTime,
} from '@m/components-ui/date-and-time/useCalendar';
import { useFeatureGate } from '@m/hooks/data/featureFlags';
import useLocation from '@m/hooks/useLocation';
import { Search } from '@m/icons';
import { CenterCoordinate } from '@m/types/MapTypes';
import { parseDeepLinkDateTime } from '@m/utils/dateTimeUtils';
import { withAttributionParams } from '@m/utils/reservationFunnelContext';
import { LatLngSchema } from '@m-service/engagement-api/google/type/latlng_pb';
import { ReservationForm } from 'apps/customer/containers/DiscoveryMap/components/ReservationForm';
import {
  ReservationSearch,
  SearchDestination,
} from 'apps/customer/containers/DiscoveryMap/components/ReservationSearch';
import { DiscoveryQueryParams } from 'apps/customer/containers/DiscoveryMap/hooks/useDiscoveryData';
import { useDiscoverySearch } from 'apps/customer/containers/DiscoveryMap/hooks/useDiscoverySearch';
import {
  isSyntheticLocationLabel,
  PARKING_NEAR_ME,
} from 'apps/customer/containers/DiscoveryMap/utils';
import { addHours, format } from 'date-fns';

import { FIND_PARKING_URL, SIGN_IN_URL } from '@/constants/links';
import { buildFindParkingUrl } from '@/services/buildParkingUrls';
import { NearMeCoordinates, resolveNearMeCoordinates } from '@/services/resolveNearMeCoordinates';

import type { ParkingSearchTarget } from '@/services/buildParkingUrls';
import type { LocationSearchParams } from '@m/types/api/LocationSuggestion';

const HERO_IMAGE = '/img/discovery-landing-hero.jpg';
const DEFAULT_START_TIME = '12:00';
const DEFAULT_END_TIME = '14:00';
const DEFAULT_DURATION_HOURS = 2;

const SEARCH_PLACEHOLDER = 'Enter an address';
/** Suggestions add venues and events to the geocoder's address-only results. */
const SUGGESTIONS_SEARCH_PLACEHOLDER = 'Enter an address, venue or event';

type HeroSearchError = 'address' | 'near-me';

const SEARCH_ERROR_COPY: Record<HeroSearchError, string> = {
  address: 'Please enter a destination.',
  'near-me': "We couldn't find your location. Enter a destination to search.",
};

function endFromStart(startDate: DateValue, time: string, durationMs?: number) {
  const start = dateValueToDate(startDate, time);
  const end = new Date(start.getTime() + (durationMs ?? DEFAULT_DURATION_HOURS * 60 * 60 * 1000));
  return { time: format(end, 'HH:mm'), value: dateToDateValue(end) };
}

function toTimestamp(date: Date) {
  return create(TimestampSchema, {
    seconds: BigInt(Math.floor(date.getTime() / 1000)),
    nanos: (date.getTime() % 1000) * 1_000_000,
  });
}

function combineDateAndTime(
  dateValue: DateValue | undefined,
  time: string | undefined,
): Date | undefined {
  if (!dateValue || !time) return undefined;
  return dateValueToDate(dateValue, time);
}

export function LandingHero() {
  const [, setCenterCoordinate] = useState<CenterCoordinate | null>(null);
  const [, setQueryParams] = useState<DiscoveryQueryParams | null>(null);

  const { position } = useLocation({
    trigger: false,
    options: { enableHighAccuracy: true, timeout: 5000, maximumAge: 60000 },
  });

  /** Coordinates resolved by a near-me search, kept so the geocoder can bias to them. */
  const [nearMeLocation, setNearMeLocation] = useState<NearMeCoordinates | null>(null);

  const userLocation = useMemo(() => {
    if (position.latitude === null || position.longitude === null) return nearMeLocation;
    return { latitude: position.latitude, longitude: position.longitude };
  }, [nearMeLocation, position.latitude, position.longitude]);

  const mapCenter = useMemo(
    () =>
      userLocation
        ? ([userLocation.longitude, userLocation.latitude] as [number, number])
        : undefined,
    [userLocation],
  );

  const search = useDiscoverySearch(
    mapCenter,
    userLocation,
    setCenterCoordinate,
    setQueryParams,
    undefined,
    undefined,
    { autoFillSyntheticLocation: false },
  );

  const [startValue, setStartValue] = useState<DateValue[]>([]);
  const [startTime, setStartTime] = useState(DEFAULT_START_TIME);
  const [endValue, setEndValue] = useState<DateValue[]>([]);
  const [endTime, setEndTime] = useState(DEFAULT_END_TIME);
  const [searchError, setSearchError] = useState<HeroSearchError | null>(null);
  const [mobileFormOpen, setMobileFormOpen] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  /** Guards against a second near-me lookup while the first one is still resolving. */
  const locatingRef = useRef(false);

  const timeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const hasCommittedSearchLocation = search.selectedSearchLoc !== null;

  const { data: freeTextSearchGate } = useFeatureGate('growth_free-text-search-enabled');
  const searchPlaceholder = freeTextSearchGate?.enabled
    ? SUGGESTIONS_SEARCH_PLACEHOLDER
    : SEARCH_PLACEHOLDER;

  // Prefill Starts/Ends on mount (and keep them) so every section stays usable
  // before a location is chosen — matches discovery landing Figma.
  useEffect(() => {
    const start = snapToSelectableTime(new Date());
    const end = addHours(start, DEFAULT_DURATION_HOURS);
    setStartValue([dateToDateValue(start)]);
    setStartTime(format(start, 'HH:mm'));
    setEndValue([dateToDateValue(end)]);
    setEndTime(format(end, 'HH:mm'));
  }, []);

  const startDateTime = startValue[0] ? dateValueToDate(startValue[0], startTime) : undefined;
  const endDateTime = endValue[0] ? dateValueToDate(endValue[0], endTime) : undefined;
  const startTimeOptions = getSelectableTimeOptions(startDateTime);
  const endTimeOptions = getSelectableTimeOptions(endDateTime, startDateTime);

  // The prefilled/previously-picked start can fall in the past (e.g. the "now"
  // default going stale, or picking "today" after noon) — snap it forward to
  // the first still-selectable option, carrying the existing start→end gap
  // forward too (so a 2h prefill stays 2h instead of collapsing to whatever
  // slot happens to be next).
  useEffect(() => {
    if (!startValue[0] || startTimeOptions.length === 0) return;
    if (startTimeOptions.some(({ value }) => value === startTime)) return;
    const correctedStart = startTimeOptions[0].value;
    setStartTime(correctedStart);
    if (!endValue[0]) return;
    const durationMs =
      dateValueToDate(endValue[0], endTime).getTime() -
      dateValueToDate(startValue[0], startTime).getTime();
    const nextEnd = endFromStart(startValue[0], correctedStart, Math.max(durationMs, 0));
    setEndValue([nextEnd.value]);
    setEndTime(nextEnd.time);
  }, [startValue, startTime, startTimeOptions, endValue, endTime]);

  // Only independently re-snap the end when the start is already valid —
  // when the start also needs correcting, the effect above owns it (and
  // preserves the gap) so this stays a no-op for that render.
  useEffect(() => {
    if (startValue[0] && !startTimeOptions.some(({ value }) => value === startTime)) return;
    if (!endValue[0] || endTimeOptions.length === 0) return;
    if (endTimeOptions.some(({ value }) => value === endTime)) return;
    setEndTime(endTimeOptions[0].value);
  }, [startValue, startTime, startTimeOptions, endValue, endTime, endTimeOptions]);

  // Force a re-render periodically so a prefilled/picked window that goes
  // stale while the hero sits idle (no clicks/typing) still gets caught by
  // the snap-forward effects above, instead of only on the next interaction.
  const [, forceRevalidate] = useState(0);
  useEffect(() => {
    const id = setInterval(() => forceRevalidate((tick) => tick + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const navigateFindParkingWithDates = useCallback(
    (
      startDate: Date | undefined,
      endDate: Date | undefined,
      selected?: {
        address: string;
        eventEnd?: string;
        eventStart?: string;
        latitude: number;
        longitude: number;
        relatedVenueId?: string;
        searchParams?: LocationSearchParams;
        venueName?: string;
      },
    ) => {
      const loc = selected ?? search.selectedSearchLoc;
      let target: ParkingSearchTarget | undefined;
      const eventId = loc?.searchParams?.eventId;
      const venueId = loc?.searchParams?.venueId ?? loc?.relatedVenueId;
      const zoneId = loc?.searchParams?.zoneId;
      if (eventId) {
        target = {
          eventId,
          type: 'event',
          venueId,
        };
      } else if (venueId) {
        target = { type: 'venue', venueId };
      } else if (zoneId) {
        target = { type: 'zone', zoneId };
      }

      // Event handoffs must encode ticket times so buildFindParkingUrl can
      // derive the cushioned parking window. Hero picker dates are for
      // address/venue/zone searches only.
      let windowStart = startDate;
      let windowEnd = endDate;
      if (eventId) {
        const eventStart = loc?.eventStart ? parseDeepLinkDateTime(loc.eventStart) : undefined;
        const eventEnd = loc?.eventEnd ? parseDeepLinkDateTime(loc.eventEnd) : undefined;
        if (eventStart) {
          windowStart = eventStart;
          windowEnd = eventEnd;
        }
      }

      const href = buildFindParkingUrl({
        location: loc?.address ?? (search.searchQuery.trim() || undefined),
        coordinates: loc
          ? create(LatLngSchema, { latitude: loc.latitude, longitude: loc.longitude })
          : undefined,
        start: windowStart ? toTimestamp(windowStart) : undefined,
        end: windowEnd ? toTimestamp(windowEnd) : undefined,
        target,
        timeZone: windowStart || windowEnd ? timeZone : undefined,
        venueName: loc?.venueName,
      });

      window.location.assign(withAttributionParams(href || FIND_PARKING_URL));
    },
    [search.searchQuery, search.selectedSearchLoc, timeZone],
  );

  const navigateFindParking = useCallback(
    (selected?: {
      address: string;
      eventEnd?: string;
      eventStart?: string;
      latitude: number;
      longitude: number;
      relatedVenueId?: string;
      searchParams?: LocationSearchParams;
      venueName?: string;
    }) => {
      let startDate = combineDateAndTime(startValue[0], startTime);
      let endDate = combineDateAndTime(endValue[0], endTime);

      // Fresh clock check at the moment of submit — a prefilled/picked window
      // can go stale while the hero sits open. Correct forward (preserving
      // the original start→end gap) and redirect with the corrected window
      // instead of sending a dead time range.
      if (startDate) {
        const correctedStart = snapToSelectableTime(startDate);
        if (correctedStart.getTime() !== startDate.getTime()) {
          const durationMs = endDate ? endDate.getTime() - startDate.getTime() : undefined;
          startDate = correctedStart;
          endDate =
            durationMs !== undefined ? new Date(correctedStart.getTime() + durationMs) : endDate;
          setStartValue([dateToDateValue(correctedStart)]);
          setStartTime(format(correctedStart, 'HH:mm'));
          if (endDate) {
            setEndValue([dateToDateValue(endDate)]);
            setEndTime(format(endDate, 'HH:mm'));
          }
        }
      }

      navigateFindParkingWithDates(startDate, endDate, selected);
    },
    [endTime, endValue, navigateFindParkingWithDates, startTime, startValue],
  );

  /**
   * Resolves the visitor's location, commits "Parking near me" onto the field and
   * — for a submit that was waiting on it — continues straight into Find parking
   * with the resolved coordinates (rather than waiting on a committed-pin render).
   */
  const startNearMeSearch = useCallback(
    ({
      navigateOnCommit = false,
      onError,
    }: { navigateOnCommit?: boolean; onError?: () => void } = {}) => {
      if (locatingRef.current) return;
      locatingRef.current = true;
      setIsLocating(true);
      setSearchError(null);

      resolveNearMeCoordinates(userLocation)
        .then((coords) => {
          if (!coords) {
            setSearchError('near-me');
            onError?.();
            return;
          }
          setNearMeLocation(coords);
          search.commitParkingNearMe(coords);
          if (navigateOnCommit) {
            navigateFindParking({ address: PARKING_NEAR_ME, ...coords });
          }
        })
        .finally(() => {
          locatingRef.current = false;
          setIsLocating(false);
        });
    },
    [navigateFindParking, search, userLocation],
  );

  const onUseCurrentLocation = useCallback(
    (onError?: () => void) => startNearMeSearch({ onError }),
    [startNearMeSearch],
  );

  const runFindParking = useCallback((): boolean => {
    if (hasCommittedSearchLocation || isSyntheticLocationLabel(search.searchQuery)) {
      setSearchError(null);
      navigateFindParking();
      return true;
    }

    if (search.searchQuery.trim()) {
      const first = search.searchResultsCurrent ? search.destinations[0] : undefined;
      if (first) {
        search.handleSearchLocation(first);
        const [longitude, latitude] = first.center;
        setSearchError(null);
        navigateFindParking({
          address: first.place_name,
          eventEnd: first.eventEnd,
          eventStart: first.eventStart,
          latitude,
          longitude,
          relatedVenueId: first.relatedVenueId,
          searchParams: first.searchParams,
          venueName: first.venueName ?? first.address,
        });
        return true;
      }

      if (search.handleSearch()) {
        setSearchError(null);
        navigateFindParking();
        return true;
      }

      setSearchError('address');
      return false;
    }

    // Empty location → standard Parking Near Me + prefilled times flow.
    startNearMeSearch({ navigateOnCommit: true });
    return true;
  }, [hasCommittedSearchLocation, navigateFindParking, search, startNearMeSearch]);

  const handleSearchLocation = useCallback(
    (dest: SearchDestination) => {
      search.handleSearchLocation(dest);
      setSearchError(null);
    },
    [search],
  );

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    runFindParking();
  };

  const windowLabel =
    startDateTime && endDateTime
      ? `${format(startDateTime, 'MMM d, h:mm a')} - ${format(endDateTime, 'MMM d, h:mm a')}`
      : undefined;

  const searchProps = {
    searchQuery: search.searchQuery,
    onSearchQueryChange: search.setSearchQuery,
    onRestoreSearchQuery: search.restoreSearchQuery,
    destinations: search.destinations,
    recentSearches: search.recentSearches,
    allowClearLocation: false,
    invalid: searchError !== null,
    errorText: searchError ? SEARCH_ERROR_COPY[searchError] : undefined,
    showEmptyState: search.showSearchEmptyState,
    selectedSuggestionType: search.selectedSearchLoc?.suggestionType,
    onSearch: runFindParking,
    onSelectDestination: handleSearchLocation,
    onUseCurrentLocation,
    placeholder: searchPlaceholder,
  };

  return (
    <>
      {/* Mobile hero — Figma 22857:329553 */}
      <Box
        display={{ base: 'block', lg: 'none' }}
        w="full"
        maxW="100%"
        minW={0}
        overflowX="clip"
        position="relative"
        minH="690px"
        css={{
          backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.7) 100%), url(${HERO_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Box pt={4} px={4} pb={8} w="full" maxW="100%" minW={0} position="absolute" bottom={0}>
          <Stack gap={8} w="full" minW={0}>
            <VStack align="start" gap={4} w="full" minW={0}>
              <Text
                variant="Header.H1"
                color="text.white"
                fontSize="40px"
                lineHeight="48px"
                w="full"
                maxW="100%"
              >
                Book your parking with ease
              </Text>
              <Text variant="MainBodyMedium" color="text.white" w="full" maxW="100%">
                Wherever you go, Metropolis is always ticket-free, hassle-free and checkout-free.
              </Text>
            </VStack>
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              bg="fill.input.enabled"
              borderRadius="md"
              px={4}
              h="56px"
              w="full"
              minW={0}
              boxShadow="0px 5px 15px var(--shadow-color)"
              boxShadowColor="black/18"
              cursor="pointer"
              role="button"
              tabIndex={0}
              aria-label={
                search.searchQuery.trim()
                  ? `${search.searchQuery.trim()}${windowLabel ? `, ${windowLabel}` : ''}`
                  : searchPlaceholder
              }
              onClick={() => setMobileFormOpen(true)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setMobileFormOpen(true);
                }
              }}
            >
              <VStack align="start" gap={0} flex="1" minW={0}>
                <Text
                  variant="SmallBodyMedium"
                  color={search.searchQuery.trim() ? 'text.primary' : 'text.secondary'}
                  truncate
                  w="full"
                >
                  {search.searchQuery.trim() || searchPlaceholder}
                </Text>
                {windowLabel && (
                  <Text variant="ExtraSmallBodyRegular" color="text.secondary" truncate w="full">
                    {windowLabel}
                  </Text>
                )}
              </VStack>
              <Button
                type="button"
                variant="brand"
                size="sm"
                aria-label="Find parking"
                loading={isLocating}
                onClick={(event) => {
                  event.stopPropagation();
                  runFindParking();
                }}
              >
                <Search aria-hidden />
              </Button>
            </Box>
          </Stack>
        </Box>
      </Box>

      {mobileFormOpen && (
        <ReservationForm
          authenticated={false}
          onLoginClick={() => {
            window.location.assign(withAttributionParams(SIGN_IN_URL));
          }}
          autoCorrectExpiredWindow
          lockReservationWindow
          locating={isLocating}
          initialStartDateTime={startDateTime}
          initialEndDateTime={endDateTime}
          destinations={search.destinations}
          hasCommittedSearchLocation={hasCommittedSearchLocation}
          onBack={(draft) => {
            if (draft?.startDateTime) {
              setStartValue([dateToDateValue(draft.startDateTime)]);
              setStartTime(format(draft.startDateTime, 'HH:mm'));
            }
            if (draft?.endDateTime) {
              setEndValue([dateToDateValue(draft.endDateTime)]);
              setEndTime(format(draft.endDateTime, 'HH:mm'));
            }
            setMobileFormOpen(false);
          }}
          onFindParking={(startDate, endDate) => {
            navigateFindParkingWithDates(startDate, endDate);
            setMobileFormOpen(false);
          }}
          onSearch={search.handleSearch}
          onSearchQueryChange={search.setSearchQuery}
          onRestoreSearchQuery={search.restoreSearchQuery}
          onSelectDestination={search.handleSearchLocation}
          onUseCurrentLocation={onUseCurrentLocation}
          placeholder={searchPlaceholder}
          recentSearches={search.recentSearches}
          nearMeErrorText={SEARCH_ERROR_COPY['near-me']}
          searchQuery={search.searchQuery}
          showSearchEmptyState={search.showSearchEmptyState}
        />
      )}

      {/* Desktop hero — Figma 22787:179283 */}
      <Box
        display={{ base: 'none', lg: 'flex' }}
        w="full"
        minH="608px"
        position="relative"
        alignItems="flex-end"
        justifyContent="center"
        px="158px"
        pb="80px"
        css={{
          backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.45) 55%, rgba(0,0,0,0.65) 100%), url(${HERO_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Stack as="form" gap="8" w="full" maxW="1126px" minW={0} onSubmit={handleSubmit}>
          <Stack gap="4" maxW="736px">
            <Text
              variant="Header.H1"
              color="text.white"
              fontSize="64px"
              lineHeight="72px"
              letterSpacing="-0.5px"
            >
              Book your parking with ease
            </Text>
            <Text variant="Subheader" color="text.white" maxW="535px">
              Wherever you go, Metropolis is always ticket-free, hassle-free and checkout-free.
            </Text>
          </Stack>

          <Flex align="flex-end" gap="4" w="full" minW={0} flexWrap="wrap">
            <Box flex="1 1 320px" minW={0} maxW="434px">
              <VStack align="stretch" gap="1" w="full" minW={0}>
                <Text variant="SmallBodyMedium" color="text.white">
                  Where?
                </Text>
                <ReservationSearch
                  presentation="dropdown"
                  compact
                  showSearchButton
                  overlayErrorText
                  {...searchProps}
                />
              </VStack>
            </Box>

            <Box
              flex="1 1 200px"
              minW={0}
              maxW="261px"
              css={{
                '& [data-part="label"], & label': { color: 'var(--chakra-colors-text-white)' },
              }}
            >
              <DatePicker
                type="datetime"
                selectionMode="single"
                size="md"
                label="Starts"
                placeholder="Select your start time"
                value={startValue}
                min={dateToDateValue(new Date())}
                startTime={startTime}
                startTimeOptions={startTimeOptions}
                defaultStartTime={DEFAULT_START_TIME}
                clearable={false}
                onStartTimeChange={(time) => {
                  setStartTime(time);
                  if (!startValue[0]) return;
                  const nextEnd = endFromStart(startValue[0], time);
                  setEndValue([nextEnd.value]);
                  setEndTime(nextEnd.time);
                }}
                onValueChange={(details) => {
                  if (!details.value[0]) return;
                  setStartValue(details.value);
                  const nextEnd = endFromStart(details.value[0], startTime);
                  setEndValue([nextEnd.value]);
                  setEndTime(nextEnd.time);
                }}
              />
            </Box>

            <Box
              flex="1 1 200px"
              minW={0}
              maxW="261px"
              css={{
                '& [data-part="label"], & label': { color: 'var(--chakra-colors-text-white)' },
              }}
            >
              <DatePicker
                type="datetime"
                selectionMode="single"
                size="md"
                label="Ends"
                placeholder="Select your end time"
                value={endValue}
                startTime={endTime}
                startTimeOptions={endTimeOptions}
                defaultStartTime={DEFAULT_END_TIME}
                min={startValue[0]}
                clearable={false}
                onStartTimeChange={setEndTime}
                onValueChange={(details) => {
                  if (!details.value[0]) return;
                  setEndValue(details.value);
                }}
              />
            </Box>

            <Button
              type="submit"
              variant="brand"
              size="md"
              flexShrink={0}
              alignSelf="flex-end"
              loading={isLocating}
              loadingText="Finding your location"
            >
              Find parking
            </Button>
          </Flex>
        </Stack>
      </Box>
    </>
  );
}
